import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertyAPI, projectAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { 
  Search, 
  Loader2, 
  MapPin, 
  Home,
  Bed,
  Bath,
  Square,
  Calendar,
  DollarSign,
  Car,
  Plus,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Sparkles,
  Edit3,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const PROPERTY_TYPES = [
  { value: 'single_family', label: 'Single Family' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'multi_family', label: 'Multi Family' },
  { value: 'land', label: 'Land' },
];

const PARKING_OPTIONS = [
  { value: 'garage', label: 'Garage' },
  { value: 'driveway', label: 'Driveway' },
  { value: 'carport', label: 'Carport' },
  { value: 'street', label: 'Street' },
  { value: 'none', label: 'None' },
];

const PropertySearchPage = () => {
  const navigate = useNavigate();
  const [searchForm, setSearchForm] = useState({
    address: '',
    city: '',
    state: '',
    zipcode: ''
  });
  const [searching, setSearching] = useState(false);
  const [propertyData, setPropertyData] = useState(null);
  const [editableProperty, setEditableProperty] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchForm.address.trim()) {
      toast.error('Please enter an address');
      return;
    }
    
    setSearching(true);
    setPropertyData(null);
    setEditableProperty(null);
    setIsEditing(false);
    
    try {
      const response = await propertyAPI.search(searchForm);
      
      if (response.data.success) {
        setPropertyData(response.data);
        setEditableProperty({ ...response.data.property });
        toast.success('Property found! You can edit the details below.');
      } else {
        setPropertyData(response.data);
        setEditableProperty({ ...response.data.property });
        toast.warning('Limited data available. Please review and edit as needed.');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handlePropertyChange = (field, value) => {
    setEditableProperty(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateProject = async () => {
    if (!editableProperty) return;
    
    setCreating(true);
    const prop = editableProperty;
    
    try {
      const projectData = {
        name: `${prop.address} Flip`,
        address: prop.address,
        city: prop.city,
        state: prop.state,
        zipcode: prop.zipcode,
        property_type: prop.property_type || 'single_family',
        status: 'planning',
        purchase_price: parseFloat(prop.estimated_value) * 0.75 || 0,
        estimated_arv: parseFloat(prop.estimated_value) || 0,
        target_budget: parseFloat(prop.estimated_value) * 0.15 || 0,
        beds: parseInt(prop.beds) || 0,
        baths: parseFloat(prop.baths) || 0,
        sqft: parseInt(prop.sqft) || 0,
        lot_size: parseInt(prop.lot_size) || 0,
        year_built: parseInt(prop.year_built) || 0,
        notes: `Property found via AI search.\n\nFeatures: ${prop.features?.join(', ') || 'N/A'}\n\n${prop.description || ''}`
      };
      
      const response = await projectAPI.create(projectData);
      toast.success('Project created successfully!');
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      console.error('Create project error:', error);
      toast.error('Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold font-heading tracking-tight">Property Search</h1>
        <p className="text-muted-foreground mt-1">
          Search for property details and create a new project
        </p>
      </div>

      {/* Search Form */}
      <Card className="border-border bg-card rounded-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            AI-Powered Property Search
          </CardTitle>
          <CardDescription>
            Enter an address to get property details - you can edit all values after search
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  placeholder="123 Main Street"
                  value={searchForm.address}
                  onChange={(e) => setSearchForm({ ...searchForm, address: e.target.value })}
                  className="bg-secondary border-border rounded-sm"
                />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  placeholder="Austin"
                  value={searchForm.city}
                  onChange={(e) => setSearchForm({ ...searchForm, city: e.target.value })}
                  className="bg-secondary border-border rounded-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    placeholder="TX"
                    maxLength={2}
                    value={searchForm.state}
                    onChange={(e) => setSearchForm({ ...searchForm, state: e.target.value.toUpperCase() })}
                    className="bg-secondary border-border rounded-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="zipcode">Zip Code</Label>
                  <Input
                    id="zipcode"
                    placeholder="78701"
                    maxLength={5}
                    value={searchForm.zipcode}
                    onChange={(e) => setSearchForm({ ...searchForm, zipcode: e.target.value })}
                    className="bg-secondary border-border rounded-sm"
                  />
                </div>
              </div>
            </div>
            
            <Button 
              type="submit" 
              disabled={searching || !searchForm.address.trim()}
              className="w-full md:w-auto bg-primary text-primary-foreground rounded-sm"
            >
              {searching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Search Property
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Search Results - Editable */}
      {propertyData && editableProperty && (
        <Card className="border-border bg-card rounded-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  {propertyData.success ? (
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-400" />
                  )}
                  Property Details
                  <Badge variant="outline" className="ml-2 rounded-sm text-xs">
                    <Edit3 className="w-3 h-3 mr-1" />
                    Editable
                  </Badge>
                </CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="rounded-sm text-xs">
                    {propertyData.source}
                  </Badge>
                  <span className="text-xs">All fields can be adjusted before creating project</span>
                </CardDescription>
              </div>
              <Button 
                onClick={handleCreateProject}
                disabled={creating}
                className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Project
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Address Section - Editable */}
            <div className="p-4 bg-secondary/30 rounded-sm space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Home className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">Location Details</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="prop-address">Street Address</Label>
                  <Input
                    id="prop-address"
                    value={editableProperty.address || ''}
                    onChange={(e) => handlePropertyChange('address', e.target.value)}
                    className="bg-secondary border-border rounded-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="prop-city">City</Label>
                  <Input
                    id="prop-city"
                    value={editableProperty.city || ''}
                    onChange={(e) => handlePropertyChange('city', e.target.value)}
                    className="bg-secondary border-border rounded-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="prop-state">State</Label>
                    <Input
                      id="prop-state"
                      value={editableProperty.state || ''}
                      maxLength={2}
                      onChange={(e) => handlePropertyChange('state', e.target.value.toUpperCase())}
                      className="bg-secondary border-border rounded-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="prop-zipcode">Zip Code</Label>
                    <Input
                      id="prop-zipcode"
                      value={editableProperty.zipcode || ''}
                      maxLength={5}
                      onChange={(e) => handlePropertyChange('zipcode', e.target.value)}
                      className="bg-secondary border-border rounded-sm"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="prop-type">Property Type</Label>
                  <Select 
                    value={editableProperty.property_type || 'single_family'}
                    onValueChange={(value) => handlePropertyChange('property_type', value)}
                  >
                    <SelectTrigger className="bg-secondary border-border rounded-sm">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Property Specs - Editable */}
            <div className="p-4 bg-secondary/30 rounded-sm space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Square className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">Property Specifications</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="prop-beds" className="flex items-center gap-1">
                    <Bed className="w-3 h-3" /> Bedrooms
                  </Label>
                  <Input
                    id="prop-beds"
                    type="number"
                    min="0"
                    max="20"
                    value={editableProperty.beds || ''}
                    onChange={(e) => handlePropertyChange('beds', parseInt(e.target.value) || 0)}
                    className="bg-secondary border-border rounded-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="prop-baths" className="flex items-center gap-1">
                    <Bath className="w-3 h-3" /> Bathrooms
                  </Label>
                  <Input
                    id="prop-baths"
                    type="number"
                    min="0"
                    max="20"
                    step="0.5"
                    value={editableProperty.baths || ''}
                    onChange={(e) => handlePropertyChange('baths', parseFloat(e.target.value) || 0)}
                    className="bg-secondary border-border rounded-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="prop-sqft" className="flex items-center gap-1">
                    <Square className="w-3 h-3" /> Sq Ft
                  </Label>
                  <Input
                    id="prop-sqft"
                    type="number"
                    min="0"
                    value={editableProperty.sqft || ''}
                    onChange={(e) => handlePropertyChange('sqft', parseInt(e.target.value) || 0)}
                    className="bg-secondary border-border rounded-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="prop-lot" className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Lot Size (sqft)
                  </Label>
                  <Input
                    id="prop-lot"
                    type="number"
                    min="0"
                    value={editableProperty.lot_size || ''}
                    onChange={(e) => handlePropertyChange('lot_size', parseInt(e.target.value) || 0)}
                    className="bg-secondary border-border rounded-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="prop-year" className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Year Built
                  </Label>
                  <Input
                    id="prop-year"
                    type="number"
                    min="1800"
                    max="2025"
                    value={editableProperty.year_built || ''}
                    onChange={(e) => handlePropertyChange('year_built', parseInt(e.target.value) || 0)}
                    className="bg-secondary border-border rounded-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="prop-parking" className="flex items-center gap-1">
                    <Car className="w-3 h-3" /> Parking
                  </Label>
                  <Select 
                    value={editableProperty.parking || 'garage'}
                    onValueChange={(value) => handlePropertyChange('parking', value)}
                  >
                    <SelectTrigger className="bg-secondary border-border rounded-sm">
                      <SelectValue placeholder="Select parking" />
                    </SelectTrigger>
                    <SelectContent>
                      {PARKING_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Financial Details - Editable */}
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-sm space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                <h3 className="font-semibold text-green-400">Financial Estimates</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="prop-value" className="text-green-400">Estimated Value ($)</Label>
                  <Input
                    id="prop-value"
                    type="number"
                    min="0"
                    step="1000"
                    value={editableProperty.estimated_value || ''}
                    onChange={(e) => handlePropertyChange('estimated_value', parseFloat(e.target.value) || 0)}
                    className="bg-secondary border-green-500/30 rounded-sm text-green-400 font-mono"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Used as ARV for project</p>
                </div>
                <div>
                  <Label htmlFor="prop-last-sale">Last Sale Price ($)</Label>
                  <Input
                    id="prop-last-sale"
                    type="number"
                    min="0"
                    step="1000"
                    value={editableProperty.last_sale_price || ''}
                    onChange={(e) => handlePropertyChange('last_sale_price', parseFloat(e.target.value) || null)}
                    className="bg-secondary border-border rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label htmlFor="prop-tax">Tax Assessment ($)</Label>
                  <Input
                    id="prop-tax"
                    type="number"
                    min="0"
                    step="100"
                    value={editableProperty.tax_assessment || ''}
                    onChange={(e) => handlePropertyChange('tax_assessment', parseFloat(e.target.value) || null)}
                    className="bg-secondary border-border rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label htmlFor="prop-hoa">HOA Fee ($/month)</Label>
                  <Input
                    id="prop-hoa"
                    type="number"
                    min="0"
                    step="10"
                    value={editableProperty.hoa_fee || ''}
                    onChange={(e) => handlePropertyChange('hoa_fee', parseFloat(e.target.value) || null)}
                    className="bg-secondary border-border rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label htmlFor="prop-sale-date">Last Sale Date</Label>
                  <Input
                    id="prop-sale-date"
                    type="date"
                    value={editableProperty.last_sale_date || ''}
                    onChange={(e) => handlePropertyChange('last_sale_date', e.target.value || null)}
                    className="bg-secondary border-border rounded-sm"
                  />
                </div>
              </div>
            </div>

            {/* Description - Editable */}
            <div className="space-y-2">
              <Label htmlFor="prop-description">Property Description</Label>
              <Textarea
                id="prop-description"
                rows={3}
                value={editableProperty.description || ''}
                onChange={(e) => handlePropertyChange('description', e.target.value)}
                className="bg-secondary border-border rounded-sm"
                placeholder="Enter property description..."
              />
            </div>

            {/* Quick Stats Summary */}
            <div className="p-4 bg-primary/10 border border-primary/20 rounded-sm">
              <h4 className="font-semibold mb-3">Project Preview (based on edits)</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Purchase Price (75%)</p>
                  <p className="font-mono font-bold">{formatCurrency((editableProperty.estimated_value || 0) * 0.75)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Rehab Budget (15%)</p>
                  <p className="font-mono font-bold">{formatCurrency((editableProperty.estimated_value || 0) * 0.15)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Target ARV</p>
                  <p className="font-mono font-bold text-green-400">{formatCurrency(editableProperty.estimated_value || 0)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Est. Profit (10%)</p>
                  <p className="font-mono font-bold text-green-400">{formatCurrency((editableProperty.estimated_value || 0) * 0.10)}</p>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            {propertyData.disclaimer && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-sm">
                <p className="text-xs text-amber-400 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  {propertyData.disclaimer}
                </p>
              </div>
            )}

            {/* Create Project CTA */}
            <div className="flex justify-end pt-4 border-t border-border">
              <Button 
                onClick={handleCreateProject}
                disabled={creating}
                size="lg"
                className="bg-primary text-primary-foreground rounded-sm"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating Project...
                  </>
                ) : (
                  <>
                    Create Project with These Details
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tips Section */}
      {!propertyData && (
        <Card className="border-border bg-card rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Search Tips</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                Enter the full street address for best results
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                Include city, state, and zip code for more accurate data
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <strong>All property values are editable</strong> - adjust beds, baths, sqft, estimated value, etc.
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                Click "Create Project" to start tracking a flip with your customized data
              </li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PropertySearchPage;
